"use client"

import {X, Link} from "lucide-react";
import React from "react";
import {Button} from "@/components/ui/button";
import {IconGoogleDrive, IconJira, IconLinear} from "@/app/components/icons/Icons";
import {Skeleton} from "@/components/ui/skeleton";

/**
 * Props for the LinkPreviewItem component.
 */
export interface LinkPreviewItemProps {
  /** The link provider (e.g., "jira", "linear") */
  provider: string;
  /** The identifier (issue key, doc ID, etc.) */
  identifier: string;
  /** Display title */
  title?: string;
  /** Status label */
  status?: string;
  /** URL to open */
  url: string;
  /** Callback when the remove button is clicked */
  onRemove: () => void;
  /** Whether data is still loading */
  isLoading?: boolean;
}

/**
 * Fallback icon for unknown providers.
 */
function FallbackIcon({className}: {className?: string}) {
  return <Link className={className} />;
}

/**
 * Provider display configuration.
 * Add new providers here with their icons and display names.
 */
const providerConfig: Record<string, {
  icon: React.ComponentType<{className?: string}>;
  name: string;
}> = {
  jira: { icon: IconJira, name: "Jira" },
  linear: { icon: IconLinear, name: "Linear" },
  "linear-project": { icon: IconLinear, name: "Linear" },
  notion: { icon: FallbackIcon, name: "Notion" },
  "google-drive": { icon: IconGoogleDrive, name: "Google Drive" },
  confluence: { icon: FallbackIcon, name: "Confluence" },
};

/**
 * A single link preview item for display in the composition area.
 * Supports multiple providers with provider-specific icons.
 * Styled consistently with file attachments.
 */
export function LinkPreviewItem({
  provider,
  identifier,
  title,
  status,
  url,
  onRemove,
  isLoading = false,
}: LinkPreviewItemProps) {
  const config = providerConfig[provider] ?? { icon: FallbackIcon, name: "Link" };
  const Icon = config.icon;

  if (isLoading) {
    return (
      <div className="relative inline-block m-1">
        <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm">
          <Icon className="h-5 w-5 shrink-0" />
          <div className="flex flex-col gap-1">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative inline-block m-1">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm hover:bg-muted/80 transition-colors cursor-pointer"
        title={`Open ${identifier} in ${config.name}`}
      >
        <Icon className="h-5 w-5 shrink-0" />
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-foreground">{identifier}</span>
            {status && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-background text-muted-foreground">
                {status}
              </span>
            )}
          </div>
          {title && (
            <span className="text-xs text-muted-foreground truncate max-w-[200px]">
              {title}
            </span>
          )}
        </div>
      </a>
      <Button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove();
        }}
        aria-label={`Remove ${identifier}`}
        variant="ghost"
        size="icon"
        className="absolute end-1 top-1 translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-[#f4f4f4] bg-black p-[2px] text-white dark:border-token-bg-tertiary dark:bg-white dark:text-black hover:bg-black/80 dark:hover:bg-white/80 h-auto w-auto"
      >
        <X className="size-3" aria-hidden="true" />
      </Button>
    </div>
  );
}
